import React, { useState } from 'react'
import {
  makeStyles,
  createStyles,
  Button,
  TextField,
  Box,
  CircularProgress,
  InputLabel,
  Typography,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Formik } from 'formik'
import { Link, useLocation, useHistory } from 'react-router-dom'
import { omit } from 'lodash'
import {
  useTranslation,
  // Trans
} from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { signUp, signOut, getUserInfo } from 'src/store/slices/authSlice'
import { API } from '../../../store/api'
import { DIALING_CODES, AGREEMENT_ID_MAP } from 'src/utils/constant'
import CodeButton from 'src/mobile/components/CodeButton'
import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'
import TagManager from 'react-gtm-module'
import FocusError from 'src/mobile/components/FocusError'
import AuthHeader from 'src/mobile/components/Auth/AuthHeader'
import AuthTabs from 'src/mobile/components/Auth/AuthTabs'
import DialingCodeSelector from 'src/mobile/components/DialingCodeSelector'
// import useRedirectAfterLogin from 'src/hooks/useRedirectAfterLogin'
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined'
import { loginOrSignEvent } from 'src/utils/trackingCode'
import { Helmet  } from 'react-helmet'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      minHeight: `calc(100vh - ${theme.spacing(41)}px)`,
    },
    main: {
      padding: theme.spacing(0, 2.5),
      background: '#fff',
    },
    popperHelperText: {
      // marginBottom: theme.spacing(1.5),
      color: '#9CA3AF',
    },
    formControlLabel: {
      color: theme.palette.grey[200],
    },
    continueTip: {
      fontWeight: ' 600 !important',
      fontSize: '26px',
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        paddingTop: theme.spacing(1),
        fontSize: theme.typography.h2.fontSize,
      },
      [theme.breakpoints.down('xs')]: {
        paddingTop: theme.spacing(2),
      },
    },
    paddingForm: {
      backgroundColor: '#fff',
      paddingTop: 0,
      paddingBottom: theme.spacing(0.5),
    },
    inputLabel: {
      fontWeight: 400,
      color: '#111827',
      fontSize: 14,
      marginTop: 5,
    },
    helperText: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
    },
    link: {
      textDecoration: 'underline',
    },
    submit: {
      height: 56,
      fontSize: 18,
      fontWeight: 600,
      [theme.breakpoints.down('xs')]: {
        fontSize: 16,
      },
    },
  }),
)

const initialValues = {
  verificationType: 'phone',
  email: '',
  phone: '',
  dialingCode: DIALING_CODES[0].value,
  code: '',
  password: '',
  confirmPassword: '',
  // joinClub: false,
}

const SignUp = ({ source }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  // const redirectAfterLogin = useRedirectAfterLogin()
  const { signUpStatus, userInfoStatus } = useSelector((state) => state.auth)
  const [signupStep, setSignupStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const location = useLocation()
  const { pathname } = location
  const history = useHistory()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const schema = oriSchema(t).pick([
    'verificationType',
    'email',
    'dialingCode',
    'phone',
    'code',
    'password',
    'confirmPassword',
    // 'joinClub',
  ])

  const handleSubmit = async (values) => {
    values.language = i18n.language
    if (signUpStatus === 'pending' || userInfoStatus === 'pending') return
    const omitKeys =
      values.verificationType === 'email'
        ? ['verificationType', 'phone', 'dialingCode', 'confirmPassword', 'code']
        : ['verificationType', 'email', 'confirmPassword', 'code']
    const pureValues = omit(values, omitKeys)
    dispatch(signOut())
    await dispatch(signUp(pureValues)).unwrap()
    await dispatch(getUserInfo()).unwrap()
    TagManager.dataLayer({
      dataLayer: {
        event: 'signupComplete',
      },
    })
    toast.success(t('tips.sign_up_successfully'))
    // tracking code
    const method =  values.verificationType === 'phone' ? 'phone' : 'email'
    loginOrSignEvent('sign_up', method)
    history.push(pathname === `/${i18n.language}/add-order/signup` ? `/${i18n.language}/add-order` : '/')
    // return redirectAfterLogin({
    //   redirectUrl: pathname === `/${i18n.language}/add-order/signup` ? `/${i18n.language}/add-order` : '/',
    // })
  }

  const handleValidateCode = async (values) => {
    const data =
      values.verificationType === 'phone'
        ? { phone: values.phone, code: values.code, dialingCode: values.dialingCode }
        : { email: values.email, code: values.code }
    // console.log(data)
    await API.codeVerify(data)
    setSignupStep(2)
  }
  return (
    <Box width="100%" maxWidth={630} margin="0 auto" className={classes.root}>
      <Helmet>
        <meta name="description" content={t('seoDescription.signup')} />
        <title>{t('seoTitle.signup')}</title>
      </Helmet>   
      {source !== 'order' && (
        <AuthHeader
          title={t('common.sign_up_user')}
          goStepOne={() => {
            setSignupStep(1)
          }}
          showLogo={source === 'order' && isMobile === 'order' ? true : false}
          step={signupStep}
          source={source}
        />
      )}

      <Box className={classes.main}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {(props) => {
            const { values, setFieldTouched, setFieldValue, handleSubmit, handleChange, errors, touched } =
              props

            useTranslateFormErrors(errors, touched, setFieldTouched)

            const onBeforeGetCode = async () => await schema.validateAt(values.verificationType, values)

            const onGetCodeError = () => setFieldTouched(values.verificationType)

            const isError = (field) => touched[field] && Boolean(errors[field])
            const errorText = (field) => touched[field] && errors[field]

            const handleTabsChange = (value) => {
              value && setFieldValue('verificationType', value)
            }

            return (
              <form
                onSubmit={handleSubmit}
                className={source === 'order' ? classes.paddingForm : classes.form}
              >
                {source === 'order' && !isMobile && (
                  <Typography variant="h3" className={classes.continueTip}>
                    {t('common.signup_continue')}
                  </Typography>
                )}
                {signupStep === 2 ? (
                  ''
                ) : (
                  <AuthTabs
                    onChange={handleTabsChange}
                    activeTab={values.verificationType}
                    authType={t('common.register')}
                    hideLogo={source === 'order' ? true : false}
                  />
                )}

                {source === 'order' && isMobile && (
                  <Typography variant="h3" className={classes.continueTip}>
                    {t('common.signup_continue')}
                  </Typography>
                )}
                {signupStep === 1 && (
                  <>
                    {values.verificationType === 'phone' ? (
                      <Box>
                        <InputLabel classes={{ root: classes.inputLabel }}>
                          {t('form.mobile_phone')}
                        </InputLabel>
                        <Box my={1}>
                          <TextField
                            // margin="normal"
                            fullWidth
                            id="phone"
                            name="phone"
                            variant="outlined"
                            value={values.phone}
                            onChange={handleChange}
                            error={isError('phone')}
                            helperText={errorText('phone')}
                            FormHelperTextProps={{
                              classes: {
                                root: classes.helperText,
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <DialingCodeSelector
                                  value={values.dialingCode}
                                  onChange={(dialingCode) => setFieldValue('dialingCode', dialingCode)}
                                />
                              ),
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <InputLabel classes={{ root: classes.inputLabel }}>
                          {t('common.email_address')}
                        </InputLabel>
                        <Box my={1}>
                          <TextField
                            fullWidth
                            id="email"
                            name="email"
                            variant="outlined"
                            value={values.email}
                            onChange={handleChange}
                            error={isError('email')}
                            helperText={errorText('email')}
                            FormHelperTextProps={{
                              classes: {
                                root: classes.helperText,
                              },
                            }}
                          />
                        </Box>
                      </>
                    )}
                    <CodeButton
                      type={values.verificationType}
                      action="signup"
                      params={
                        values.verificationType === 'email'
                          ? { [values.verificationType]: values[values.verificationType] }
                          : {
                              dialingCode: values.dialingCode,
                              [values.verificationType]: values[values.verificationType],
                            }
                      }
                      onBeforeGetCode={onBeforeGetCode}
                      onGetCodeError={onGetCodeError}
                    />
                    <InputLabel classes={{ root: classes.inputLabel }}>{t('form.input_code')}</InputLabel>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="code"
                      name="code"
                      variant="outlined"
                      value={values.code}
                      onChange={handleChange}
                      error={isError('code')}
                      FormHelperTextProps={{
                        classes: {
                          root: classes.helperText,
                        },
                      }}
                      helperText={errorText('code')}
                    />
                  </>
                )}
                {signupStep === 2 && (
                  <Box mt={source === 'order' ? 0 : 3}>
                    <InputLabel classes={{ root: classes.inputLabel }}>{t('form.password')}</InputLabel>
                    <TextField
                      margin="normal"
                      fullWidth
                      name="password"
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      placeholder={t('validation.please_enter', { field: `$t(form.password)` })}
                      value={values.password}
                      onChange={handleChange}
                      error={isError('password')}
                      FormHelperTextProps={{
                        classes: {
                          root: classes.helperText,
                        },
                      }}
                      helperText={errorText('password')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {!showPassword ? (
                                <VisibilityOffOutlined color="primary" />
                              ) : (
                                <VisibilityOutlined color="primary" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" className={classes.popperHelperText}>
                      {t('validation.password_match', { field: '$t(form.password)' })}
                    </Typography>
                    <Box mt={2}>
                      <InputLabel classes={{ root: classes.inputLabel }}>
                        {t('form.confirm_password')}
                      </InputLabel>
                      <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        variant="outlined"
                        placeholder={t('validation.please_enter', { field: `$t(form.confirm_password)` })}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        error={isError('confirmPassword')}
                        helperText={errorText('confirmPassword')}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.helperText,
                          },
                        }}
                      />
                    </Box>
                    {/* <FormControl error={isError('joinClub')}>
                    <FormControlLabel
                      classes={{
                        label: classes.formControlLabel,
                      }}
                      control={<ECheckbox size="small" color="secondary" />}
                      label={
                        <Box component="span">
                          {t('tips.i_agree_join')}
                          <Link
                            to={`/agreement/${AGREEMENT_ID_MAP.extraCarePrivacyPolicy.id}`}
                            target="_blank"
                          >
                            <Box color="primary.main" component="span">
                              {t(AGREEMENT_ID_MAP.extraCarePrivacyPolicy.label)}
                            </Box>
                          </Link>
                          、
                          <Link to={`/agreement/${AGREEMENT_ID_MAP.extraCareClub.id}`} target="_blank">
                            <Box color="primary.main" component="span">
                              {t(AGREEMENT_ID_MAP.extraCareClub.label)}
                            </Box>
                          </Link>
                          ; {t('tips.and_agree_join')}
                          {t('glossary.take2_club_plan')}
                        </Box>
                      }
                      onChange={handleChange}
                      name="joinClub"
                    />
                    {errorText('joinClub') && (
                      <FormHelperText error={touched.joinClub && Boolean(errors.joinClub)}>
                        {errorText('joinClub')}
                      </FormHelperText>
                    )}
                  </FormControl> */}
                  </Box>
                )}

                <Box mt={3}>
                  {signupStep === 1 ? (
                    <Box>
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        className={classes.submit}
                        disabled={!values.code}
                        onClick={() => {
                          handleValidateCode(values)
                        }}
                      >
                        {t('common.go_on')}
                      </Button>
                      <Box my={2} fontSize="body2.fontSize" justifyContent="center" display="flex">
                        <Link
                          to={
                            source === 'order'
                              ? `/${i18n.language}/add-order/signin`
                              : `/${i18n.language}/signin`
                          }
                          replace
                        >
                          <Box color="text.primary" component="span">
                            {t('are_you_sure.have_an_account')}
                            <Box component="span" borderBottom="1px solid">
                              {t('common.sign_in_now')}
                            </Box>
                          </Box>
                        </Link>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        className={classes.submit}
                      >
                        {signUpStatus === 'pending' || userInfoStatus === 'pending' ? (
                          <CircularProgress color="inherit" size={24} />
                        ) : (
                          t('common.create_account')
                        )}
                      </Button>
                      <Box my={2}>
                        {t('common.create_account_tip')}
                        <Link
                          className={classes.link}
                          to={`/${i18n.language}/agreement/${AGREEMENT_ID_MAP.extraCareClub.id}`}
                          target="_blank"
                        >
                          <Box color="primary.main" component="span">
                            {t(AGREEMENT_ID_MAP.extraCarePrivacyPolicy.label)}
                          </Box>
                        </Link>
                        {t('common.and')}
                        <Link
                          className={classes.link}
                          to={`/${i18n.language}/agreement/${AGREEMENT_ID_MAP.extraCarePrivacyPolicy.id}`}
                          target="_blank"
                        >
                          <Box color="primary.main" component="span">
                            {t(AGREEMENT_ID_MAP.extraCareClub.label)}
                          </Box>
                        </Link>
                      </Box>
                    </>
                  )}
                </Box>
                <FocusError />
              </form>
            )
          }}
        </Formik>
      </Box>
    </Box>
  )
}

export default SignUp
